import { extractNumber } from '@/services/currency.service'
import * as Sentry from '@sentry/browser'
import { keyBy } from 'lodash-es'
import {
  addCompensation,
  addCompensations,
  bulkUpdateCompensations,
  getCompensations,
  updateCompensation,
  updateCompensations
} from '@/services/compensations-api.service.js'
import { errorBus, personUpdateBus } from '@/services/eventbus.service.js'
import { logger } from '@/utils/logger'

const state = {
  compensations: {},
  compensationByPersonId: {},
  isFetchingCompensations: {}
}

const ensureBoardCompensationState = (_state, boardId) => {
  if (!Array.isArray(_state.compensations[boardId])) _state.compensations[boardId] = []
  if (
    !_state.compensationByPersonId[boardId] ||
    Array.isArray(_state.compensationByPersonId[boardId])
  ) {
    _state.compensationByPersonId[boardId] = {}
  }
}

const getters = {
  compensations: (_state) => (boardId) => {
    return _state.compensations[boardId]?.filter((compensation) => !compensation?.deletedAt)
  },
  compensationForPerson:
    (_state) =>
    ({ boardId, personId }) => {
      return _state.compensationByPersonId[boardId]?.[personId] || null
    },
  salaryNumber:
    (_state, _getters) =>
    ({ boardId, personId }) => {
      const payRate = _getters.compensationForPerson({ boardId, personId })?.payRate
      return extractNumber(payRate)
    },
  salaryHistory:
    (_state, _getters) =>
    ({ boardId, personId }) => {
      const previousRates =
        _getters.compensationForPerson({ boardId, personId })?.previousRates || []
      return previousRates?.map((salary) => extractNumber(salary.payRate))
    },
  compensationByPersonId: (_state) => (boardId) => _state.compensationByPersonId[boardId] || {},
  isFetchingCompensations: (_state) => (boardId) => _state.isFetchingCompensations[boardId] ?? false
}

const mutations = {
  clearCompensations(_state, boardId) {
    _state.compensations[boardId] = []
    _state.compensationByPersonId[boardId] = {}
  },
  setCompensations(_state, { data, boardId }) {
    const normalizedCompensations = Array.isArray(data) ? data : []
    _state.compensations[boardId] = normalizedCompensations
    _state.compensationByPersonId[boardId] = keyBy(normalizedCompensations, 'personId')
  },
  addCompensation(_state, { data, boardId }) {
    ensureBoardCompensationState(_state, boardId)

    _state.compensations[boardId].push(data)
    _state.compensationByPersonId[boardId][data.personId] = data
  },
  setCompensation(_state, { data, boardId }) {
    ensureBoardCompensationState(_state, boardId)

    const index = _state.compensations[boardId].findIndex(
      ({ personId }) => data.personId === personId
    )

    if (index === -1) {
      _state.compensations[boardId].push(data)
    } else {
      _state.compensations[boardId][index] = data
    }

    _state.compensationByPersonId[boardId][data.personId] = data
  },
  updateCompensations(_state, { compensations, boardId }) {
    //find exising compensations first
    ensureBoardCompensationState(_state, boardId)

    const compIdToIndex = {}
    _state.compensations[boardId].forEach((comp, index) => {
      compIdToIndex[comp.id] = index
    })

    compensations.forEach((comp) => {
      const index = compIdToIndex[comp.id]
      if (index === undefined) {
        _state.compensations[boardId].push(comp)
      } else {
        _state.compensations[boardId][index] = comp
      }
    })

    _state.compensationByPersonId[boardId] = keyBy(_state.compensations[boardId], 'personId')
  },
  setIsFetchingCompensations(_state, { boardId, status }) {
    _state.isFetchingCompensations[boardId] = status
  }
}

const actions = {
  async fetchNew(context, { boardId, force = true }) {
    if (context.state.compensations[boardId] && !force) return false
    if (context.state.isFetchingCompensations[boardId]) return false

    await context.dispatch('fetchCompensations', { boardId })
    return true
  },

  async fetchCompensations(context, { boardId, force = true }) {
    try {
      if (!force && context.getters.compensations(boardId)?.length > 0) return
      if (!force && context.getters.isFetchingCompensations(boardId)) return

      context.commit('setIsFetchingCompensations', { boardId, status: true })

      const compensations = await getCompensations({ boardId })
      if (!Array.isArray(compensations)) throw new Error('Failed to fetch compensations')

      context.commit('setCompensations', { data: compensations, boardId })
      context.commit('setIsFetchingCompensations', { boardId, status: false })
    } catch (e) {
      logger.error(e.message)
      Sentry?.captureException(e)
      context.commit('setIsFetchingCompensations', { boardId, status: false })
    }
  },

  async addCompensation(
    context,
    { personId, boardId, payPer, payRate, payRateEffectiveDate, payType, currency }
  ) {
    if (!boardId) throw new Error('BoardId is undefined')

    const newData = {
      personId,
      boardId,
      payPer,
      payRate,
      payRateEffectiveDate,
      payType,
      currency
    }

    context.commit('addCompensation', { data: newData, boardId })

    try {
      await addCompensation({ boardId, compensation: newData })
    } catch (e) {
      logger.error('Error adding new compensation: ', e.message)
      if (Sentry) Sentry.captureException(e)
      errorBus.emit({ type: 'sync', message: e })
    }
  },

  async updateCompensation(
    context,
    {
      personId,
      boardId,
      payPer = null,
      payRate = null,
      payRateEffectiveDate = null,
      payType = null,
      currency = null,
      historyMeta = null
    }
  ) {
    if (!boardId) throw new Error('BoardId is undefined')

    const previousCompensation = context.getters.compensationForPerson({ boardId, personId })

    try {
      const newData = {
        ...(previousCompensation || {}),
        personId,
        boardId,
        payPer,
        payRate,
        payRateEffectiveDate,
        payType,
        currency,
        previousRates: [
          ...(previousCompensation?.previousRates || []),
          {
            payRate: previousCompensation?.payRate || null,
            payType: previousCompensation?.payType || null,
            currency: previousCompensation?.currency || null
          }
        ]
      }

      context.commit('setCompensation', { data: newData, boardId })

      let compensation = await updateCompensation({
        boardId,
        personId,
        compensation: newData,
        historyMeta
      })

      if (!compensation) {
        compensation = await addCompensation({ boardId, compensation: newData })
      }

      personUpdateBus.emit({ type: 'compensation', compensation })
    } catch (e) {
      logger.error('Error updating compensation: ', e.message)
      if (Sentry) Sentry.captureException(e)
      errorBus.emit({ type: 'sync', message: e })
    }
  },

  async updateCompensations(context, { boardId, compensations }) {
    if (!boardId) throw new Error('BoardId is undefined')

    try {
      const comps = await updateCompensations({ boardId, compensations })
      //TODO: update existing comps based on comps return obj
      await context.dispatch('fetchCompensations', { boardId })
      const updatedCompensations = Array.isArray(comps) && comps.length > 0 ? comps : compensations
      updatedCompensations.forEach((compensation) => {
        if (compensation?.personId) {
          personUpdateBus.emit({ type: 'compensation', compensation })
        }
      })
    } catch (e) {
      logger.error('Error updating compensation: ', e.message)
      if (Sentry) Sentry.captureException(e)
      errorBus.emit({ type: 'sync', message: e })
    }
  },

  async bulkUpdateCompensations(context, { boardId, peopleIds, compensation, historyMeta = null }) {
    if (!boardId) throw new Error('BoardId is undefined')

    try {
      await bulkUpdateCompensations({ boardId, compensation, peopleIds, historyMeta })
      await context.dispatch('fetchCompensations', { boardId })
      peopleIds.forEach((personId) => {
        personUpdateBus.emit({ type: 'compensation', compensation: { personId, boardId } })
      })
    } catch (e) {
      logger.error('Error updating compensation: ', e.message)
      if (Sentry) Sentry.captureException(e)
      errorBus.emit({ type: 'sync', message: e })
    }
  },

  async createCompensations(context, { boardId, compensations }) {
    if (!boardId) throw new Error('BoardId is undefined')
    if (!compensations || compensations.length === 0) return

    try {
      const comps = await addCompensations({ boardId, compensations })
      comps?.forEach((comp) => {
        context.commit('addCompensation', { data: comp, boardId })
      })
    } catch (e) {
      logger.error('Error creating compensation: ', e.message)
      if (Sentry) Sentry.captureException(e)
      errorBus.emit({ type: 'sync', message: e })
    }
  },

  async updateCompensationCurrency(context, { personId, boardId, currency, historyMeta = null }) {
    if (!boardId) throw new Error('BoardId is undefined')

    const previousCompensation = context.getters.compensationForPerson({ boardId, personId }) || {}
    try {
      const newData = {
        ...previousCompensation,
        currency,
        previousRates: [
          ...(previousCompensation?.previousRates || []),
          {
            payRate: previousCompensation?.payRate || null,
            payType: previousCompensation?.payType || null,
            currency: previousCompensation?.currency || null
          }
        ]
      }

      const compensation = await updateCompensation({
        boardId,
        personId,
        compensation: newData,
        historyMeta
      })
      context.commit('setCompensation', { data: compensation, boardId })
      personUpdateBus.emit({ type: 'compensation', compensation })
    } catch (e) {
      logger.error('Error updating compensation: ', e.message)
      if (Sentry) Sentry.captureException(e)
      errorBus.emit({ type: 'sync', message: e })
    }
  },

  clearCompensations(context, { boardId }) {
    context.commit('clearCompensations', boardId)
  }
}

export default {
  state,
  mutations,
  getters,
  actions
}
